/**
 * Die Adresse einer Seite, wie Patch-Auswahl, Patch-Cache und Ingest sie
 * vergleichen (scanner#355). EINE Funktion fuer Kunden-Bundle und Server: der
 * Client schickt sie als `url`, der Server wendet sie am Eingang von
 * GET /patches, beim Einlesen eines Scans (`violations.page_url`) und beim
 * Aufloesen noch einmal an — idempotent, alte Bundles bleiben so bedient.
 *
 * Nutzerentscheid 28.09.2026:
 *  - das Fragment faellt immer weg: ein Anker oder Skip-Link (`#main`) ist
 *    dieselbe Seite, und `#access_token=…` hat in einem Speicherschluessel
 *    nichts verloren;
 *  - bekannte Tracking-Parameter fallen weg (`utm_*`, `gclid`, `fbclid` und die
 *    Klick-Kennungen unten), die uebrige Query bleibt — sie kann den Inhalt
 *    bestimmen (`?page=2`, `?id=17`).
 *
 * scanner#357 (Nutzerentscheid 01.10.2026): der Schlussstrich eines Pfads
 * faellt weg, ausser an der Wurzel — `/about/` und `/about` sind eine Seite.
 * Der Crawler (`discover-urls.ts`) speichert seit jeher ohne; ein Besucher auf
 * `/about/` schickte aber `/about/`, der exakte Vergleich in GET /patches traf
 * nicht, und die Seite bekam keine Patches.
 *
 * Bewusst KEIN Neuschreiben ueber `URL`: alles, was nicht wegfaellt, bleibt
 * Byte fuer Byte stehen — Reihenfolge, Kodierung, Schreibweise. Gespeicherte
 * Adressen ohne Fragment und ohne Tracking-Parameter behalten so exakt ihre
 * Kennung; eine geaenderte Schreibweise wuerde sie von ihren Patches trennen.
 * Kein `URL`-Parser heisst auch: keine Ausnahme, auch nicht bei Unsinn.
 *
 * Klein gehalten, weil sie im Kunden-Bundle mitfaehrt (Budget, Gate 3).
 */

/**
 * Einzeln benannte Tracking-Parameter. Nur Klick- und Kampagnenkennungen, die
 * Werbe- und Mailsysteme selbst an Links haengen und die nie den Inhalt einer
 * Seite waehlen: Google Ads (gclid, gbraid, wbraid, dclid), Meta (fbclid),
 * Microsoft Ads (msclkid), Yandex (yclid), X (twclid), TikTok (ttclid),
 * LinkedIn (li_fat_id), Instagram (igshid), Google Merchant (srsltid),
 * Mailchimp (mc_cid, mc_eid), HubSpot (_hsenc, _hsmi), Google-Linker (_gl),
 * Google Analytics (_ga), Marketo (mkt_tok), Klaviyo (_kx), Impact
 * (irclickid), Awin (awc).
 */
const TRACKING_PARAMS = [
  'gclid', 'fbclid', 'gbraid', 'wbraid', 'dclid', 'msclkid', 'yclid', 'twclid',
  'ttclid', 'li_fat_id', 'igshid', 'srsltid', 'mc_cid', 'mc_eid', '_hsenc', '_hsmi', '_gl',
  // scanner#368: Google-Analytics-Linker (_ga), Marketo (mkt_tok), Klaviyo
  // (_kx), Impact (irclickid), Awin (awc). NICHT `ref`: das waehlt auf vielen
  // Seiten den Inhalt (Nutzerentscheid 28.09.: die uebrige Query bleibt).
  '_ga', 'mkt_tok', '_kx', 'irclickid', 'awc',
];

/** Ist dieser Query-Parameter (Name, wie er in der URL steht) ein Tracking-Parameter? */
export function isTrackingParam(rawName: string): boolean {
  let name = rawName;
  try { name = decodeURIComponent(rawName.replace(/\+/g, ' ')); } catch { /* kaputte Kodierung: roh vergleichen */ }
  name = name.toLowerCase();
  return name.slice(0, 4) === 'utm_' || TRACKING_PARAMS.indexOf(name) >= 0;
}

/** Seitenadresse ohne Fragment, ohne Tracking-Parameter (scanner#355) und ohne Schlussstrich (scanner#357). */
export function normalizePageUrl(url: string): string {
  return withoutTrailingSlash(withoutFragmentAnd(url, isTrackingParam));
}

/** `/about/` → `/about`, `/about//?a=1` → `/about?a=1`; die Wurzel `/` bleibt. Erwartet eine Adresse ohne Fragment. */
function withoutTrailingSlash(page: string): string {
  const q = page.indexOf('?');
  const path = q < 0 ? page : page.slice(0, q);
  const scheme = path.indexOf('//');
  const root = path.indexOf('/', scheme < 0 ? 0 : scheme + 2);
  if (root < 0) return page;
  let end = path.length;
  while (end - 1 > root && path.charAt(end - 1) === '/') end--;
  return end === path.length ? page : path.slice(0, end) + (q < 0 ? '' : page.slice(q));
}

/**
 * Query-Parameter, die der Review-Link selbst anhaengt (scanner#358): `t`
 * traegt das Review-JWT, die anderen steuern das Panel. `hbReview` ist seit
 * #199 stillgelegt, alte Links tragen es noch.
 */
const REVIEW_PARAMS = ['hbReviewPage', 'hbFocus', 'hbReview', 't'];

function isReviewParam(rawName: string): boolean {
  let name = rawName;
  try { name = decodeURIComponent(rawName.replace(/\+/g, ' ')); } catch { /* roh vergleichen */ }
  return REVIEW_PARAMS.indexOf(name) >= 0;
}

/**
 * Die Adresse, die wir nur MELDEN, nie als Patch-Kennung benutzen
 * (scanner#358): Analytics-Bericht (`analytics_reports.url`) und `url` der
 * Review-Sitzung. Wie normalizePageUrl, zusaetzlich ohne die Review-Parameter —
 * sonst stuende das Review-JWT (`?t=…`) in Datenbank und Access-Logs. Nicht fuer
 * die Patch-Auswahl: dort kann `t` ein echter Seitenparameter sein.
 */
export function reportPageUrl(url: string): string {
  return withoutFragmentAnd(url, (name) => isTrackingParam(name) || isReviewParam(name));
}

function withoutFragmentAnd(url: string, drop: (rawName: string) => boolean): string {
  const hash = url.indexOf('#');
  const page = hash < 0 ? url : url.slice(0, hash);
  const q = page.indexOf('?');
  if (q < 0) return page;
  const pairs = page.slice(q + 1).split('&');
  const kept = pairs.filter((pair) => !drop(pair.split('=')[0]));
  if (kept.length === pairs.length) return page;
  const query = kept.filter((pair) => pair !== '').join('&');
  return page.slice(0, q) + (query ? '?' + query : '');
}
